import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { workforceProtectedFormsCopySetting, workforceProtectedFormsDisplaySetting, } from './workforce-protected-form-setting-definitions.js';
export * from './workforce-protected-form-setting-definitions.js';
export const workforceDirectoryConfigurationValueSchema = z.strictObject({
    showBirthdayLabels: z.boolean(),
    customerCrewProfilesEnabled: z.boolean(),
    includeArchivedProfilesByDefault: z.boolean(),
    maximumRoleLabels: z.number().int().min(1).max(12),
});
export const workforceDirectoryConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'workforce.directory.configuration',
        moduleId: 'workforce',
        title: 'Workforce directory behavior',
        helpText: 'Controls tenant directory presentation and operational profile behavior within protected platform limits.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            showBirthdayLabels: true,
            customerCrewProfilesEnabled: false,
            includeArchivedProfilesByDefault: false,
            maximumRoleLabels: 12,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: workforceDirectoryConfigurationValueSchema,
});
export const workforceDirectoryDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(50), z.literal(100), z.literal(250)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showCompany: z.boolean(),
    showManager: z.boolean(),
    showIdentityColor: z.boolean(),
});
export const workforceDirectoryDisplaySetting = definePlatformSetting({
    definition: {
        key: 'workforce.directory.display',
        moduleId: 'workforce',
        title: 'Workforce directory display',
        helpText: 'Controls directory density, card sizing, and visible operational profile details.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 100,
            density: 'comfortable',
            cardSize: 'standard',
            showCompany: true,
            showManager: true,
            showIdentityColor: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: workforceDirectoryDisplayValueSchema,
});
export const workforceDirectoryCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    searchLabel: z.string().trim().min(1).max(80),
    departmentLabel: z.string().trim().min(1).max(80),
    allDepartmentsLabel: z.string().trim().min(1).max(80),
    jobTitleLabel: z.string().trim().min(1).max(80),
    employeeNumberLabel: z.string().trim().min(1).max(80),
    employmentStatusLabel: z.string().trim().min(1).max(80),
    employmentTypeLabel: z.string().trim().min(1).max(80),
    companyLabel: z.string().trim().min(1).max(80),
    companyCodeLabel: z.string().trim().min(1).max(80),
    managerLabel: z.string().trim().min(1).max(80),
    startDateLabel: z.string().trim().min(1).max(80),
    terminationDateLabel: z.string().trim().min(1).max(80),
    birthdayLabel: z.string().trim().min(1).max(80),
    dateOfBirthLabel: z.string().trim().min(1).max(80),
    personalEmailLabel: z.string().trim().min(1).max(80),
    secondaryPhoneLabel: z.string().trim().min(1).max(80),
    addressLabel: z.string().trim().min(1).max(80),
    addressLine2Label: z.string().trim().min(1).max(80),
    cityLabel: z.string().trim().min(1).max(80),
    stateLabel: z.string().trim().min(1).max(80),
    postalCodeLabel: z.string().trim().min(1).max(80),
    emergencyContactLabel: z.string().trim().min(1).max(80),
    emergencyRelationshipLabel: z.string().trim().min(1).max(80),
    emergencyPhoneLabel: z.string().trim().min(1).max(80),
    emergencyAlternatePhoneLabel: z.string().trim().min(1).max(80),
    emergencyNotesLabel: z.string().trim().min(1).max(80),
    employmentNotesLabel: z.string().trim().min(1).max(80),
    customerProfileTitleLabel: z.string().trim().min(1).max(80),
    customerProfileBioLabel: z.string().trim().min(1).max(80),
    customerProfileEnabledLabel: z.string().trim().min(1).max(80),
    roleLabelsLabel: z.string().trim().min(1).max(80),
    identityColorLabel: z.string().trim().min(1).max(80),
    changeReasonLabel: z.string().trim().min(1).max(80),
    profileHistoryLabel: z.string().trim().min(1).max(80),
    profileHistoryEmptyLabel: z.string().trim().min(1).max(160),
    includeArchivedLabel: z.string().trim().min(1).max(100),
    editProfileAction: z.string().trim().min(1).max(80),
    archiveProfileAction: z.string().trim().min(1).max(80),
    restoreProfileAction: z.string().trim().min(1).max(80),
    saveProfileAction: z.string().trim().min(1).max(80),
    loadingLabel: z.string().trim().min(1).max(120),
    savingLabel: z.string().trim().min(1).max(120),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    profileContributionLoading: z.string().trim().min(1).max(160),
    profileContributionError: z.string().trim().min(1).max(240),
    statusLabels: z.strictObject({
        active: z.string().trim().min(1).max(80),
        inactive: z.string().trim().min(1).max(80),
        leave: z.string().trim().min(1).max(80),
        terminated: z.string().trim().min(1).max(80),
    }),
    employmentTypeLabels: z.strictObject({
        hourly: z.string().trim().min(1).max(80),
        salary: z.string().trim().min(1).max(80),
        contractor: z.string().trim().min(1).max(80),
    }),
});
export const workforceDirectoryCopySetting = definePlatformSetting({
    definition: {
        key: 'workforce.copy.directory',
        moduleId: 'workforce',
        title: 'Workforce directory copy',
        helpText: 'Controls tenant-editable labels and messages for the employee directory and profile editor.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Workforce',
            title: 'Employee directory',
            description: 'Operational employee profiles, teams, reporting lines, and manager records.',
            searchLabel: 'Search employees',
            departmentLabel: 'Department',
            allDepartmentsLabel: 'All departments',
            jobTitleLabel: 'Job title',
            employeeNumberLabel: 'Employee number',
            employmentStatusLabel: 'Employment status',
            employmentTypeLabel: 'Employment type',
            companyLabel: 'Company',
            companyCodeLabel: 'Company code',
            managerLabel: 'Manager',
            startDateLabel: 'Start date',
            terminationDateLabel: 'Termination date',
            birthdayLabel: 'Birthday',
            dateOfBirthLabel: 'Private date of birth',
            personalEmailLabel: 'Personal email',
            secondaryPhoneLabel: 'Secondary phone',
            addressLabel: 'Street address',
            addressLine2Label: 'Address line 2',
            cityLabel: 'City',
            stateLabel: 'State',
            postalCodeLabel: 'Postal code',
            emergencyContactLabel: 'Emergency contact',
            emergencyRelationshipLabel: 'Emergency relationship',
            emergencyPhoneLabel: 'Emergency phone',
            emergencyAlternatePhoneLabel: 'Emergency alternate phone',
            emergencyNotesLabel: 'Emergency contact notes',
            employmentNotesLabel: 'Private employment notes',
            customerProfileTitleLabel: 'Customer-facing title',
            customerProfileBioLabel: 'Customer-facing biography',
            customerProfileEnabledLabel: 'Show this employee in customer crew profiles',
            roleLabelsLabel: 'Operational roles',
            identityColorLabel: 'Employee color',
            changeReasonLabel: 'Change reason',
            profileHistoryLabel: 'Profile history',
            profileHistoryEmptyLabel: 'No saved employee profile versions yet.',
            includeArchivedLabel: 'Include archived employee profiles',
            editProfileAction: 'Edit employee profile',
            archiveProfileAction: 'Archive profile',
            restoreProfileAction: 'Restore profile',
            saveProfileAction: 'Save profile version',
            loadingLabel: 'Loading employee directory…',
            savingLabel: 'Saving employee profile…',
            emptyTitle: 'No employees found',
            emptyDescription: 'No active tenant memberships match these directory filters.',
            loadError: 'The employee directory could not be loaded.',
            mutationError: 'The employee profile change could not be saved. Refresh and try again.',
            profileContributionLoading: 'Loading employee profile details…',
            profileContributionError: 'An optional employee profile detail is unavailable. The Workforce profile was not changed.',
            statusLabels: {
                active: 'Active',
                inactive: 'Inactive',
                leave: 'On leave',
                terminated: 'Terminated',
            },
            employmentTypeLabels: {
                hourly: 'Hourly',
                salary: 'Salary',
                contractor: 'Contractor',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: workforceDirectoryCopyValueSchema,
});
const workforceActivityCategoryLabelsSchema = z.strictObject({
    'late-arrival': z.string().trim().min(1).max(80),
    'left-early': z.string().trim().min(1).max(80),
    'called-off-sick': z.string().trim().min(1).max(80),
    'sick-day': z.string().trim().min(1).max(80),
    'personal-day': z.string().trim().min(1).max(80),
    'family-or-emergency': z.string().trim().min(1).max(80),
    'no-call-no-show': z.string().trim().min(1).max(80),
    'approved-time-off': z.string().trim().min(1).max(80),
    'other-attendance': z.string().trim().min(1).max(80),
    'positive-recognition': z.string().trim().min(1).max(80),
    'coaching-conversation': z.string().trim().min(1).max(80),
    'policy-violation': z.string().trim().min(1).max(80),
    'safety-observation': z.string().trim().min(1).max(80),
    'customer-feedback': z.string().trim().min(1).max(80),
    'general-note': z.string().trim().min(1).max(80),
});
export const workforceActivityCopyValueSchema = z.strictObject({
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    attendanceLabel: z.string().trim().min(1).max(80),
    behaviorLabel: z.string().trim().min(1).max(80),
    categoryLabel: z.string().trim().min(1).max(80),
    employeeLabel: z.string().trim().min(1).max(80),
    allEmployeesLabel: z.string().trim().min(1).max(80),
    sectionLabel: z.string().trim().min(1).max(80),
    allSectionsLabel: z.string().trim().min(1).max(80),
    allCategoriesLabel: z.string().trim().min(1).max(80),
    searchLabel: z.string().trim().min(1).max(80),
    occurredFromLabel: z.string().trim().min(1).max(80),
    occurredToLabel: z.string().trim().min(1).max(80),
    includeArchivedLabel: z.string().trim().min(1).max(100),
    occurredAtLabel: z.string().trim().min(1).max(80),
    noteLabel: z.string().trim().min(1).max(80),
    reasonLabel: z.string().trim().min(1).max(80),
    addAction: z.string().trim().min(1).max(80),
    updateAction: z.string().trim().min(1).max(80),
    archiveAction: z.string().trim().min(1).max(80),
    restoreAction: z.string().trim().min(1).max(80),
    exportAction: z.string().trim().min(1).max(80),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    categoryLabels: workforceActivityCategoryLabelsSchema,
});
export const workforceActivityCopySetting = definePlatformSetting({
    definition: {
        key: 'workforce.copy.activity',
        moduleId: 'workforce',
        title: 'Workforce activity copy',
        helpText: 'Controls tenant-editable labels for attendance, recognition, and coaching records.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            title: 'Employee activity',
            description: 'Retained attendance, recognition, and coaching records. Training remains a separate module.',
            attendanceLabel: 'Attendance',
            behaviorLabel: 'Behavior and coaching',
            categoryLabel: 'Category',
            employeeLabel: 'Employee',
            allEmployeesLabel: 'All employees',
            sectionLabel: 'Activity section',
            allSectionsLabel: 'All sections',
            allCategoriesLabel: 'All categories',
            searchLabel: 'Search activity notes',
            occurredFromLabel: 'Occurred from',
            occurredToLabel: 'Occurred before',
            includeArchivedLabel: 'Include archived activity records',
            occurredAtLabel: 'Date and time',
            noteLabel: 'Record note',
            reasonLabel: 'Change reason',
            addAction: 'Add activity record',
            updateAction: 'Save corrected version',
            archiveAction: 'Archive record',
            restoreAction: 'Restore record',
            exportAction: 'Download CSV',
            emptyTitle: 'No activity records',
            emptyDescription: 'Attendance, recognition, and coaching records will appear here.',
            categoryLabels: {
                'late-arrival': 'Late arrival',
                'left-early': 'Left early',
                'called-off-sick': 'Called off sick',
                'sick-day': 'Sick day',
                'personal-day': 'Personal day',
                'family-or-emergency': 'Family or emergency',
                'no-call-no-show': 'No call / no show',
                'approved-time-off': 'Approved time off',
                'other-attendance': 'Other attendance',
                'positive-recognition': 'Positive recognition',
                'coaching-conversation': 'Coaching conversation',
                'policy-violation': 'Policy violation',
                'safety-observation': 'Safety observation',
                'customer-feedback': 'Customer feedback',
                'general-note': 'General note',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: workforceActivityCopyValueSchema,
});
export const workforceSettingRegistrations = [
    workforceDirectoryConfigurationSetting,
    workforceDirectoryDisplaySetting,
    workforceDirectoryCopySetting,
    workforceActivityCopySetting,
    workforceProtectedFormsDisplaySetting,
    workforceProtectedFormsCopySetting,
];
//# sourceMappingURL=workforce-setting-definitions.js.map